import { useState } from 'react';
import type { InterviewQuestion } from '../../shared/extractInterviewQuestions';

interface Props {
  index: number;
  question: InterviewQuestion;
  /** Highlights the card and shows a "Now asking" pill while a recording
   *  is underway and this is the active question. Optional; idle render
   *  is the default. */
  active?: boolean;
}

/**
 * A single question card in the phone-friendly shoot view. Renders the
 * question text in big legible type and surfaces any authored hints as
 * tappable checkboxes underneath — the interviewer ticks them off as the
 * talent hits each cue point during the take.
 *
 * Checkbox state is intentionally ephemeral: it lives in component state
 * and resets on page reload. We don't persist per-take answers anywhere
 * (that would require a recordings model and per-interview rows). The point
 * of these boxes is real-time use on set, not historical reporting.
 */
export default function InterviewQuestionCard({ index, question, active }: Props) {
  const [checked, setChecked] = useState<Set<number>>(new Set());

  function toggle(hintIdx: number) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(hintIdx)) next.delete(hintIdx);
      else next.add(hintIdx);
      return next;
    });
  }

  return (
    <li
      className={
        'rounded-md px-5 py-5 md:px-6 md:py-6 transition-colors ' +
        (active
          ? 'bg-accent/5 border-2 border-accent shadow-sm'
          : 'bg-card border border-line')
      }
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-faint">
          Q{index + 1}
        </div>
        {active && (
          <div className="font-mono text-[10px] uppercase tracking-[0.12em] text-accent flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
            Now asking
          </div>
        )}
      </div>
      <p className="text-[19px] md:text-[22px] leading-[1.45] text-fg font-medium">
        {question.text}
      </p>

      {question.hints.length > 0 && (
        <ul className="mt-5 pt-4 border-t border-line/70 space-y-2.5">
          {question.hints.map((hint, hi) => {
            const isChecked = checked.has(hi);
            return (
              <li key={hi}>
                <label className="flex items-start gap-3 cursor-pointer select-none group">
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggle(hi)}
                    // Bigger hit target on phone — tapping anywhere on the
                    // label still toggles, but the visible box is generous.
                    className="mt-[3px] h-[20px] w-[20px] flex-none accent-accent cursor-pointer"
                  />
                  <span
                    className={
                      'text-[15px] md:text-[16px] leading-[1.45] transition-colors ' +
                      (isChecked
                        ? 'text-faint line-through'
                        : 'text-muted group-hover:text-fg')
                    }
                  >
                    {hint}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}
